import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    AlertTriangle,
    ArrowDownLeft,
    Building2,
    CalendarDays,
    FileDown,
    FileSpreadsheet,
    Gift,
    Receipt,
    RotateCcw,
    Scale,
    Search as SearchIcon,
    User as UserIcon,
} from 'lucide-react'

import { getClubLedger, getUsersForTransaction } from '../actions/walletActions'
import { listClubs } from '../actions/clubActions'
import { exportToExcel, exportToPDF } from '../utils/exportUtils'
import ScreenHeader from '../components/layout/ScreenHeader'
import {
    Badge,
    Button,
    DataTable,
    FilterCard,
    FilterToggle,
    Money,
    Notice,
    PersonCell,
    SelectInput,
    StatCard,
    StatGrid,
    TextInput,
} from '../ui'

const TYPE_TONES = {
    deposit: 'success',
    'chip-deposit': 'success',
    cashout: 'danger',
    'table-charge': 'danger',
}

// Referral rewards are stored as deposits with referenceId "REFERRAL:<userId>"
const isReferralReward = (txn) => String(txn?.referenceId || '').startsWith('REFERRAL:')

const isCreditTxn = (txn) => ['deposit', 'chip-deposit', 'cashout'].includes(txn?.transactionType)

// Display label for a transaction type ("deposit" is shown as "Top-up")
const typeLabel = (type) =>
    type === 'deposit' ? 'Top-up' : type.charAt(0).toUpperCase() + type.slice(1)

const txnTypeLabel = (txn) => {
    if (isReferralReward(txn)) return 'Referral Code'
    return txn?.transactionType ? typeLabel(txn.transactionType) : 'N/A'
}

const ClubLedger = () => {
    const dispatch = useDispatch()

    // Filters State
    const [selectedUser, setSelectedUser] = useState('')
    const [selectedClub, setSelectedClub] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [filtersOpen, setFiltersOpen] = useState(false)

    // Dropdown Data State
    const [userOptions, setUserOptions] = useState([])
    const [clubOptions, setClubOptions] = useState([])

    // Redux Selectors
    const getClubLedgerState = useSelector((state) => state.getClubLedger)
    const { loadingGetClubLedger, getClubLedgerData, errorGetClubLedger } = getClubLedgerState

    const getUsersForTransactionState = useSelector((state) => state.getUsersForTransaction)
    const { getUsersForTransactionData } = getUsersForTransactionState

    const clubList = useSelector((state) => state.clubList)
    const { clubs = [] } = clubList || {}

    // Auth Info
    const adminLogin = useSelector((state) => state.adminLogin) || {}
    const { userInfo: adminInfo } = adminLogin
    const userLogin = useSelector((state) => state.userLogin) || {}
    const { userInfo } = userLogin
    const currentUser = adminInfo || userInfo

    const isSuperAdmin = currentUser?.role?.isSuperAdmin === true

    // Initial Data Fetch
    useEffect(() => {
        dispatch(getUsersForTransaction())
        if (isSuperAdmin) {
            dispatch(listClubs({ isActive: true }))
        } else if (currentUser?.assignedClubs && currentUser.assignedClubs.length > 0) {
            // Club options are filtered down to the admin's assigned clubs below
            dispatch(listClubs({ isActive: true }))
        }

        // Fetch initial ledger (default args)
        dispatch(getClubLedger({}))

    }, [dispatch, isSuperAdmin, currentUser])

    // Update Dropdown Options
    useEffect(() => {
        if (getUsersForTransactionData && getUsersForTransactionData.users) {
            setUserOptions(getUsersForTransactionData.users.map((user) => ({
                value: user._id,
                label: `${user.name} (${user.identifier})`
            })))
        }

        if (clubs && clubs.length > 0) {
            // Filter clubs based on admin access
            let accessibleClubs = clubs
            if (!isSuperAdmin && currentUser?.assignedClubs) {
                const assignedIds = currentUser.assignedClubs.map(c => typeof c === 'object' ? c._id : c)
                accessibleClubs = clubs.filter(c => assignedIds.includes(c._id))
            }

            setClubOptions(accessibleClubs.map(c => ({
                value: c._id,
                label: `${c.clubCode} - ${c.name}`
            })))
        }
    }, [getUsersForTransactionData, clubs, isSuperAdmin, currentUser])


    // Fetch Data on Filter Change
    const handleSearch = () => {
        dispatch(getClubLedger({
            userId: selectedUser,
            clubId: selectedClub,
            startDate,
            endDate
        }))
    }

    const handleReset = () => {
        setSelectedUser('')
        setSelectedClub('')
        setStartDate('')
        setEndDate('')
        dispatch(getClubLedger({}))
    }

    // Prepare Table Data
    const transactions = getClubLedgerData?.transactions || []
    const summary = getClubLedgerData?.summary || { totalDeposits: 0, totalCashouts: 0, totalSpend: 0, totalReferral: 0, netFlow: 0 }
    const hasFilters = Boolean(selectedUser || selectedClub || startDate || endDate)
    const filterChips = [
        selectedUser && { icon: UserIcon, label: userOptions.find((o) => o.value === selectedUser)?.label || 'User' },
        selectedClub && { icon: Building2, label: clubOptions.find((o) => o.value === selectedClub)?.label || 'Club' },
        (startDate || endDate) && { icon: CalendarDays, label: `${startDate || '…'} → ${endDate || '…'}` },
    ].filter(Boolean)

    // * Export Handlers
    const prepareExportData = (dataToExport) => {
        return dataToExport.map(txn => {
            const user = txn.userId || {};
            const club = txn.clubId || {};

            return {
                'User': user.name ? `${user.name} (${user.identifier || 'No ID'})` : 'N/A',
                'Club': club.clubCode ? `${club.clubCode} - ${club.name}` : 'N/A',
                'Type': txnTypeLabel(txn).toUpperCase(),
                'Amount': txn.amount || 0,
                'Date': txn.createdAt ? new Date(txn.createdAt).toLocaleString() : 'N/A',
            };
        });
    };

    const exportColumns = [
        { title: 'Date', field: 'Date' },
        { title: 'User', field: 'User' },
        { title: 'Club', field: 'Club' },
        { title: 'Type', field: 'Type' },
        { title: 'Amount', field: 'Amount' },
    ];

    const handleExportExcel = () => {
        exportToExcel(prepareExportData(transactions), exportColumns, 'Club_User_Ledger');
    };

    const handleExportPDF = () => {
        exportToPDF(prepareExportData(transactions), exportColumns, 'Club_User_Ledger', 'Club-User Ledger Report');
    };

    const columns = [
        {
            field: 'serialNo',
            title: 'S.No',
            sortable: false,
            width: 64,
            render: (rowData, { rowIndex }) => (
                <span className="text-cm-faint tabular-nums">{rowIndex + 1}</span>
            ),
        },
        {
            field: 'createdAt',
            title: 'Date',
            render: (rowData) => (
                <span className="whitespace-nowrap">
                    {new Date(rowData.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
            ),
        },
        {
            field: 'userId.name',
            title: 'User',
            render: (rowData) => {
                const user = rowData.userId
                if (!user) return <span className="text-cm-faint">—</span>
                // Handle case where name is null/undefined but identifier exists
                return <PersonCell name={user.name || 'Unknown user'} sub={user.identifier || 'No ID'} size={28} />
            }
        },
        {
            field: 'clubId.name',
            title: 'Club',
            render: (rowData) => {
                // Handle explicit club object or missing club
                if (!rowData.clubId) return <span className="text-cm-faint">—</span>
                return (
                    <span className="inline-flex items-center gap-2 min-w-0">
                        <Badge><span className="font-mono">{rowData.clubId.clubCode || 'N/A'}</span></Badge>
                        <span className="truncate">{rowData.clubId.name || ''}</span>
                    </span>
                )
            }
        },
        {
            field: 'transactionType',
            title: 'Type',
            render: (rowData) => (
                <Badge tone={isReferralReward(rowData) ? 'gold' : TYPE_TONES[rowData.transactionType] || 'info'}>
                    {txnTypeLabel(rowData)}
                </Badge>
            )
        },
        {
            field: 'amount',
            title: 'Amount',
            align: 'right',
            render: (rowData) => {
                // Money into the player's account reads +green, money out −red (as in AdminTransaction)
                const amount = Math.abs(Number(rowData.amount) || 0)
                return <Money value={isCreditTxn(rowData) ? amount : -amount} sign className="font-semibold" />
            }
        },
    ]

    return (
        <div className='flex flex-col gap-5 min-w-0'>
            <ScreenHeader
                title="Club ledger"
                description="Money in and out between each club and its staff, with a running balance."
                actions={
                    <>
                        <FilterToggle
                            open={filtersOpen}
                            onToggle={() => setFiltersOpen((o) => !o)}
                            count={filterChips.length}
                            controls="ledger-filters"
                        />
                        <Button variant="secondary" icon={FileSpreadsheet} disabled={transactions.length === 0} onClick={handleExportExcel}>
                            Excel
                        </Button>
                        <Button variant="secondary" icon={FileDown} disabled={transactions.length === 0} onClick={handleExportPDF}>
                            PDF
                        </Button>
                    </>
                }
            />

            {/* Filters Section */}
            <FilterCard
                id="ledger-filters"
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                chips={filterChips}
                emptyLabel="All users, clubs and dates"
            >
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto] gap-3 items-end'>
                    <SelectInput
                        label="User"
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                        placeholder="All users"
                        options={userOptions}
                    />
                    <SelectInput
                        label="Club"
                        value={selectedClub}
                        onChange={(e) => setSelectedClub(e.target.value)}
                        placeholder="All clubs"
                        options={clubOptions}
                    />
                    <TextInput
                        label="From"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                    <TextInput
                        label="To"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                    <div className='flex gap-2 sm:col-span-2 lg:col-span-1'>
                        <Button icon={SearchIcon} loading={loadingGetClubLedger} onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="ghost" icon={RotateCcw} disabled={!hasFilters} onClick={handleReset}>
                            Reset
                        </Button>
                    </div>
                </div>
            </FilterCard>

            {/* Summary Cards */}
            <StatGrid cols={4}>
                <StatCard
                    label="Total top-ups"
                    icon={ArrowDownLeft}
                    tone="success"
                    loading={loadingGetClubLedger}
                    value={`₹${(summary.totalDeposits || 0).toLocaleString('en-IN')}`}
                    hint="Excludes referral rewards"
                />
                <StatCard
                    label="Total spend"
                    icon={Receipt}
                    tone="danger"
                    loading={loadingGetClubLedger}
                    value={`₹${(summary.totalSpend || 0).toLocaleString('en-IN')}`}
                    hint="Table, food & drink, membership, events"
                />
                <StatCard
                    label="Referral code total"
                    icon={Gift}
                    tone="gold"
                    loading={loadingGetClubLedger}
                    value={`₹${(summary.totalReferral || 0).toLocaleString('en-IN')}`}
                    hint="Referral rewards credited"
                />
                <StatCard
                    label="Net flow (club profit)"
                    icon={Scale}
                    tone={(summary.netFlow || 0) >= 0 ? 'success' : 'danger'}
                    loading={loadingGetClubLedger}
                    value={`${(summary.netFlow || 0) >= 0 ? '' : '−'}₹${Math.abs(summary.netFlow || 0).toLocaleString('en-IN')}`}
                    hint="Top-ups + spend"
                />
            </StatGrid>

            {/* Transactions Table */}
            {errorGetClubLedger ? (
                <Notice tone="danger" icon={AlertTriangle}>
                    {errorGetClubLedger}
                </Notice>
            ) : (
                <DataTable
                    title="Transactions"
                    columns={columns}
                    data={transactions}
                    loading={loadingGetClubLedger}
                    search={false}
                    pageSize={20}
                    pageSizeOptions={[20, 50, 100]}
                    emptyTitle="No ledger entries"
                    emptyDescription={hasFilters ? 'Nothing matches these filters. Try resetting them.' : 'Top-ups and cash-outs will appear here.'}
                />
            )}
        </div>
    )
}

export default ClubLedger
